/**
 * GatedTool — wraps an advanced tool with an email-based access gate.
 *
 *  · On mount, reads the stored token from localStorage and asks the backend
 *    if it still resolves to an approved record (server-side revocation is
 *    immediate; client cache is best-effort only).
 *  · If approved → renders the real tool component.
 *  · If not    → renders the public SEO content (always indexable) +
 *                a "Request access" form.
 *  · If a user has previously submitted, we remember their email in
 *    localStorage and show "your request is still pending — submit again
 *    to nudge us".
 *
 *  Token storage:
 *    localStorage["hv_access_token"]       — JWT issued by the backend
 *    localStorage["hv_access_email"]       — email last used (for status check)
 */
import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";

const API = process.env.REACT_APP_BACKEND_URL;

const STORAGE_TOKEN = "hv_access_token";
const STORAGE_EMAIL = "hv_access_email";

export default function GatedTool({
  toolId,                  // e.g. "pump-sizing" — sent to the backend on request
  toolTitle,               // display name
  seoDescription,          // 1-2 sentence SEO meta description
  publicIntro,             // <ReactNode> rendered above the gate (Google indexes this)
  children,                // the real tool component (renders only when verified)
}) {
  const [phase, setPhase] = useState("checking");   // checking | gated | unlocked
  const [storedEmail, setStoredEmail] = useState(localStorage.getItem(STORAGE_EMAIL) || "");
  const [pendingStatus, setPendingStatus] = useState(null);   // null | "pending" | "approved" | "revoked"

  // ---------- token verification on mount ----------
  const verifyToken = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_TOKEN);
    if (!token) {
      setPhase("gated");
      return;
    }
    try {
      const r = await fetch(`${API}/api/access/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      let data = {};
      try {
        const raw = await r.text();
        if (raw) data = JSON.parse(raw);
      } catch (_) { data = {}; }
      if (data.ok) {
        setPhase("unlocked");
      } else {
        // Token invalid or revoked — clear it but remember the email for the
        // pending-status banner.
        localStorage.removeItem(STORAGE_TOKEN);
        if (data.email) {
          localStorage.setItem(STORAGE_EMAIL, data.email);
          setStoredEmail(data.email);
          setPendingStatus(data.status || null);
        }
        setPhase("gated");
      }
    } catch (e) {
      setPhase("gated");
    }
  }, []);

  useEffect(() => { verifyToken(); }, [verifyToken]);

  // ---------- when gated, also poll for "do we have a pending request for this email?" ----------
  useEffect(() => {
    if (phase !== "gated" || !storedEmail) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/access/status?email=${encodeURIComponent(storedEmail)}`);
        let data = {};
        try {
          const raw = await r.text();
          if (raw) data = JSON.parse(raw);
        } catch (_) { data = {}; }
        if (data.status) setPendingStatus(data.status);
      } catch (_) { /* silent */ }
    })();
  }, [phase, storedEmail]);

  if (phase === "checking") {
    return (
      <div style={{ padding: "60px 24px", textAlign: "center", color: "rgba(12,42,64,0.55)" }}>
        <div className="font-mono uppercase" style={{ fontSize: "0.65rem", letterSpacing: "0.22em" }}>
          Verifying access…
        </div>
      </div>
    );
  }

  if (phase === "unlocked") {
    const docTitle = `${toolTitle} — HidroVerse`;
    return (
      <>
        <Helmet>
          <title>{docTitle}</title>
          <meta name="description" content={seoDescription} />
        </Helmet>
        {/* Small "you are in" strip + sign-out option */}
        <div
          className="mb-3 px-4 py-2 flex items-center justify-between flex-wrap gap-2"
          style={{ background: "#F3FBFB", border: "1px solid rgba(31,182,193,0.30)" }}
          data-testid="gated-tool-signed-in"
        >
          <span className="font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.65)" }}>
            Signed in as <span style={{ color: "#0C2A40", fontWeight: 700 }}>{storedEmail || "approved user"}</span>
          </span>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem(STORAGE_TOKEN);
              setPhase("gated");
            }}
            data-testid="gated-tool-signout"
            className="font-mono uppercase"
            style={{ fontSize: "0.6rem", letterSpacing: "0.18em", color: "rgba(12,42,64,0.6)", borderBottom: "1px dashed rgba(12,42,64,0.35)" }}
          >
            Sign out of this device
          </button>
        </div>
        {children}
      </>
    );
  }

  // -------------------- GATED VIEW --------------------
  const docTitle = `${toolTitle} — HidroVerse`;
  return (
    <>
      <Helmet>
        <title>{docTitle}</title>
        <meta name="description" content={seoDescription} />
      </Helmet>
      {/* Public SEO content stays visible / indexable */}
      <div data-testid="gated-tool-public-intro">{publicIntro}</div>

      {/* Request form */}
      <RequestAccessForm
        toolId={toolId}
        toolTitle={toolTitle}
        defaultEmail={storedEmail}
        pendingStatus={pendingStatus}
        onApprovedImmediately={() => {
          // Internal addresses get auto-approved + magic-link emailed.
          // We can't unlock here automatically (we don't have the JWT yet)
          // but we update the banner so they know to check their inbox.
          setPendingStatus("approved");
        }}
      />
    </>
  );
}

// =============================================================================
// REQUEST ACCESS FORM
// =============================================================================
function RequestAccessForm({ toolId, toolTitle, defaultEmail, pendingStatus, onApprovedImmediately }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [name, setName]   = useState("");
  const [company, setCompany] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);    // { status, message, nudged }
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      setError("Email and name are required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/access/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, company, tool: toolId, note }),
      });
      // Defensive body read — some browser extensions (password managers,
      // page translators) consume the fetch body before we get to it.
      // Use .text() so we make exactly one read, then parse manually.
      let data = {};
      try {
        const raw = await r.text();
        if (raw) data = JSON.parse(raw);
      } catch (_) {
        // Body unreadable (extension interference or non-JSON response).
        if (!r.ok) {
          throw new Error(`Server returned ${r.status}. Please try again, or email info@hidroverse.co.za to request access manually.`);
        }
        // 2xx but unreadable — treat as a soft success.
        data = { status: "submitted" };
      }
      if (!r.ok) throw new Error(data.detail || `Could not submit request (HTTP ${r.status}).`);
      localStorage.setItem(STORAGE_EMAIL, email.trim().toLowerCase());
      setResult(data);
      if (data.status === "approved") onApprovedImmediately?.();
    } catch (e) {
      setError(e.message || "Could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="mt-6 p-6 md:p-7"
      style={{ background: "#0C2A40", color: "#F5F7FA" }}
      data-testid="gated-tool-form"
    >
      <div
        className="font-mono uppercase mb-3"
        style={{ fontSize: "0.62rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}
      >
        Engineering-grade tool · request access
      </div>
      <p className="font-light leading-relaxed mb-5" style={{ fontSize: "0.95rem", color: "rgba(245,247,250,0.85)" }}>
        The {toolTitle} workflow is reserved for verified engineering and procurement
        contacts. We open it on request — typically within one working day — so
        each user is on file with us and gets useful follow-up if their numbers
        flag something we can help with.
      </p>

      {/* Pending banner — when the visitor has a request already on file */}
      {pendingStatus === "pending" && !result && (
        <div
          className="mb-5 p-4"
          style={{ background: "rgba(31,182,193,0.10)", border: "1px solid rgba(31,182,193,0.35)" }}
          data-testid="gated-tool-pending-banner"
        >
          <div className="font-mono uppercase mb-1" style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "#1FB6C1", fontWeight: 700 }}>
            Your request is on file
          </div>
          <p style={{ fontSize: "0.85rem", lineHeight: 1.55 }}>
            We've received your earlier request from <strong style={{ color: "#1FB6C1" }}>{defaultEmail}</strong>{" "}
            and the HidroVerse team will review it shortly. Re-submit below to
            nudge us, or check your inbox for the activation link once it's approved.
          </p>
        </div>
      )}

      {pendingStatus === "revoked" && !result && (
        <div
          className="mb-5 p-4"
          style={{ background: "rgba(184,92,56,0.12)", border: "1px solid rgba(184,92,56,0.40)" }}
          data-testid="gated-tool-revoked-banner"
        >
          <p style={{ fontSize: "0.85rem", lineHeight: 1.55 }}>
            Access for <strong>{defaultEmail}</strong> has been revoked.
            Please contact the HidroVerse team directly if this was unexpected.
          </p>
        </div>
      )}

      {result ? (
        <div
          className="p-5"
          style={{
            background: result.status === "approved" ? "rgba(31,182,193,0.12)" : "rgba(31,182,193,0.06)",
            border: `1px solid ${result.status === "approved" ? "rgba(31,182,193,0.45)" : "rgba(31,182,193,0.25)"}`,
          }}
          data-testid="gated-tool-result"
        >
          <div className="font-mono uppercase mb-2" style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "#1FB6C1", fontWeight: 700 }}>
            {result.status === "approved" ? "Approved — check your inbox" : result.nudged ? "Nudged — thank you" : "Request received"}
          </div>
          <p style={{ fontSize: "0.9rem", lineHeight: 1.6 }}>{result.message}</p>
          {result.status === "approved" && (
            <p className="mt-3 font-light" style={{ fontSize: "0.78rem", color: "rgba(245,247,250,0.7)" }}>
              Open the activation link from the email on this device. Your session lasts indefinitely until we revoke it.
            </p>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
          <GFInput label="Your name *" value={name} onChange={setName} testid="gated-name" autoFocus />
          <GFInput label="Work email *" type="email" value={email} onChange={setEmail} testid="gated-email"
                   help="Internal @hidroverse.co.za addresses are auto-approved." />
          <GFInput label="Company / organisation" value={company} onChange={setCompany} testid="gated-company" />
          <GFInput label="What are you sizing? (optional)" value={note} onChange={setNote} testid="gated-note"
                   help="Project type, location, scale — helps us give you faster follow-up." />
          {error && (
            <div className="md:col-span-2 px-3 py-2"
              style={{ background: "rgba(184,92,56,0.15)", border: "1px solid rgba(184,92,56,0.40)", color: "#FFC9B0" }}
              data-testid="gated-tool-error"
            >
              {error}
            </div>
          )}
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="gated-tool-submit"
              className="inline-block px-6 py-3 font-mono uppercase"
              style={{
                background: submitting ? "rgba(31,182,193,0.5)" : "#1FB6C1",
                color: "#0C2A40",
                fontSize: "0.72rem",
                letterSpacing: "0.18em",
                fontWeight: 700,
                border: "none",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "Sending…" : pendingStatus === "pending" ? "Nudge HidroVerse" : "Request access"}
            </button>
            <p className="mt-3 font-light" style={{ fontSize: "0.72rem", color: "rgba(245,247,250,0.55)", lineHeight: 1.55 }}>
              We use your email only to grant access and follow up. No newsletters, no marketing list — operational only.
            </p>
          </div>
        </form>
      )}
    </div>
  );
}

function GFInput({ label, value, onChange, type = "text", help, testid, autoFocus = false }) {
  return (
    <label className="block">
      <span className="font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "rgba(245,247,250,0.7)", fontWeight: 600 }}>
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        data-testid={testid}
        className="block w-full mt-1.5 px-3 py-2.5 outline-none"
        style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(31,182,193,0.30)",
          color: "#F5F7FA",
          fontSize: "0.95rem",
          fontWeight: 500,
        }}
      />
      {help && (
        <span className="mt-1 block font-light" style={{ fontSize: "0.72rem", color: "rgba(245,247,250,0.55)", lineHeight: 1.45 }}>
          {help}
        </span>
      )}
    </label>
  );
}
